import { useMemo, useState } from 'react';

import { Icon } from '../../shared/Icon';
import { TagsInputOption } from '../../shared/TagsInput/TagsInput';
import { TagsInput } from '../../shared';

import { useTags } from '../../hooks/tags';

import { RouteTo } from '../../routes';

import { Tag } from '../../types/tagTypes';

export type TagRelationsInputProps = {
    label: string;
    value: number[];
    roValue?: number[];
    onChange: (tagIds: number[]) => void;
    excludedTagIds?: number[];
    disabled?: boolean;
    placeholder?: string;
};

const CREATE_OPTION_ID = -1;

const getTagLabel = (tag: Pick<Tag, 'name' | 'stringId'>): string =>
    tag.name.en?.trim() || tag.stringId;

export const TagRelationsInput = ({
    label,
    value,
    roValue = [],
    onChange,
    excludedTagIds = [],
    disabled = false,
    placeholder = 'Type to search tags',
}: TagRelationsInputProps) => {
    const { tags, isLoading } = useTags();
    const [searchValue, setSearchValue] = useState('');

    const tagsById = useMemo(
        () => new Map(tags.map((tag) => [tag.id, tag])),
        [tags],
    );

    const tagOptions = useMemo<TagsInputOption[]>(
        () =>
            tags
                .filter((tag) => !excludedTagIds.includes(tag.id))
                .map((tag) => ({ id: tag.id, label: getTagLabel(tag) })),
        [excludedTagIds, tags],
    );

    const trimmedSearch = searchValue.trim();

    const hasExactMatch = useMemo(
        () =>
            trimmedSearch.length > 0 &&
            tagOptions.some(
                (option) =>
                    option.label.toLowerCase() === trimmedSearch.toLowerCase(),
            ),
        [tagOptions, trimmedSearch],
    );

    const options = useMemo<TagsInputOption[]>(() => {
        if (!trimmedSearch || hasExactMatch) {
            return tagOptions;
        }

        const createOption: TagsInputOption = {
            id: CREATE_OPTION_ID,
            label: `Create tag "${trimmedSearch}"`,
            icon: <Icon name="add" />,
        };

        return [createOption, ...tagOptions];
    }, [hasExactMatch, tagOptions, trimmedSearch]);

    const selectedItems = useMemo<TagsInputOption[]>(
        () =>
            value
                .map((tagId) => tagsById.get(tagId))
                .filter((tag): tag is Tag => tag !== undefined)
                .map((tag) => ({ id: tag.id, label: getTagLabel(tag) })),
        [tagsById, value],
    );

    const readOnlyItems = useMemo<TagsInputOption[]>(
        () =>
            [...new Set(roValue)]
                .filter((tagId) => !value.includes(tagId))
                .sort((left, right) => left - right)
                .map((tagId) => tagsById.get(tagId))
                .filter((tag): tag is Tag => tag !== undefined)
                .map((tag) => ({
                    id: tag.id,
                    label: getTagLabel(tag),
                })),
        [roValue, tagsById, value],
    );

    const openCreateTagPage = (name: string) => {
        const url = `${RouteTo.TagNew()}?name=${encodeURIComponent(name)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleChange = (items: TagsInputOption[]) => {
        const createSelection = items.find(
            (item) => item.id === CREATE_OPTION_ID,
        );

        if (createSelection) {
            openCreateTagPage(trimmedSearch);
            setSearchValue('');
            return;
        }

        onChange(
            [...new Set(items.map((item) => item.id))].sort(
                (left, right) => left - right,
            ),
        );
    };

    return (
        <TagsInput
            label={label}
            options={options}
            value={selectedItems}
            roValue={readOnlyItems}
            isLoading={isLoading}
            disabled={disabled}
            placeholder={placeholder}
            noOptionsText="No tags"
            onChange={handleChange}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
        />
    );
};
